import React, { type Ref, FC, useMemo } from 'react';
import { Container, Section } from '@printdeal/ui-library';
import { getSectionData } from '../helpers';
import { HomepageSectionProps } from '../types';
import { ProductProps } from '../product-list/types';
import LayoutSection from '../../layout-section/LayoutSection';
import { PopularProducts } from '../product-list/popular-products/PopularProducts';
import { NewProducts } from '../product-list/new-products/NewProducts';

export interface ProductListSectionProps {
  section?: HomepageSectionProps;
  scrollCuesInView: boolean;
  setScrollCuesInView: (inView: boolean) => void;
  onCueClick: () => void;
  popularProductSectionNode: Ref<HTMLDivElement> | null;
}

export const ProductListSection: FC<ProductListSectionProps> = ({
  section,
  scrollCuesInView,
  setScrollCuesInView,
  onCueClick,
  popularProductSectionNode,
}) => {
  const {
    popularProductsContent,
    newProductsContent,
  } = useMemo(
    () => ({
      popularProductsContent: getSectionData(section, 'popular_product_list') as ProductProps,
      newProductsContent: getSectionData(section, 'new_product_list') as ProductProps,
    }),
    [section],
  );

  const renderPopularProducts = Object.keys(popularProductsContent).length !== 0;
  const renderNewProducts = Object.keys(newProductsContent).length !== 0;

  const defaultBackground = renderNewProducts ? 'dim-branded' : 'primary-branded';

  return (
    <>
      {(renderPopularProducts || renderNewProducts) && (
        <Section
          as={LayoutSection}
          withSidebar={false}
          withDivider={false}
          backgroundColor=""
          variant={section?.backgroundStyle || defaultBackground}
          className="pd-py-14"
        >
          <Container
            data-testid="product-list"
          >
            {renderPopularProducts && (
              <PopularProducts
                popularProducts={popularProductsContent}
                ref={popularProductSectionNode}
                scrollCuesInView={scrollCuesInView}
                onCueClick={onCueClick}
                setScrollCuesInView={setScrollCuesInView}
              />
            )}
            {renderNewProducts && (
              <NewProducts
                newProducts={newProductsContent}
                scrollCuesInView={scrollCuesInView}
                setScrollCuesInView={setScrollCuesInView}
              />
            )}
          </Container>
        </Section>
      )}
    </>
  );
};
